import { memo, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  ProgressBar,
  Stack,
} from 'react-bootstrap';

import classNames from 'classnames';

import Icon from '@/components/Icon';
import Modal from '@/components/Modal';

import AiAnswerImporter from './AiAnswerImporter';
import {
  analyzeGeoAnswer,
  appendEvidenceSources,
  appendGeoAnswerTemplate,
  parseEvidenceSources,
} from './utils';

import './index.scss';

interface Props {
  content: string;
  className?: string;
  onChangeContent?: (content: string) => void;
}

const scoreVariant = (score: number) => {
  if (score >= 85) {
    return 'success';
  }
  if (score >= 60) {
    return 'warning';
  }
  return 'danger';
};

const AnswerGeoAssistant = ({ content, className, onChangeContent }: Props) => {
  const [visible, setVisible] = useState(false);
  const [sourceInput, setSourceInput] = useState('');
  const analysis = useMemo(() => analyzeGeoAnswer(content), [content]);
  const parsedSources = useMemo(
    () => parseEvidenceSources(sourceInput),
    [sourceInput],
  );
  const failedChecks = analysis.checks.filter((check) => !check.passed);
  const passedChecks = analysis.checks.filter((check) => check.passed);
  const variant = scoreVariant(analysis.score);

  const applyTemplate = () => {
    onChangeContent?.(appendGeoAnswerTemplate(content));
  };

  const applyEvidenceSources = () => {
    if (parsedSources.length === 0) {
      return;
    }
    onChangeContent?.(appendEvidenceSources(content, sourceInput));
    setSourceInput('');
  };

  return (
    <>
      <Button
        type="button"
        variant="primary"
        className="geo-answer-assistant-trigger"
        onClick={() => setVisible(true)}>
        <Icon type="bi" name="stars" className="me-2" />
        GEO 回答助手
        <Badge bg="light" text="dark" className="ms-2">
          {analysis.score}
        </Badge>
      </Button>

      <Modal
        visible={visible}
        title="GEO 回答助手"
        className="geo-answer-assistant-modal"
        bodyClass="geo-answer-assistant-modal-body"
        scrollable
        cancelText="close"
        cancelBtnVariant="primary"
        showConfirm={false}
        onCancel={() => setVisible(false)}>
        <section className={classNames('geo-answer-assistant', className)}>
          <div className="geo-assistant-head">
            <div>
              <div className="geo-assistant-title">
                <Icon type="bi" name="stars" className="me-2" />
                GEO 回答助手
                <Badge bg={variant} className="ms-2">
                  {analysis.score}
                </Badge>
              </div>
              <p className="geo-assistant-desc">
                优先检查结论是否短、解决方案是否靠前；原因、验证方法、注意事项和来源可作为折叠详情保留，不影响
                GEO/AEO 完整内容。
              </p>
            </div>
            {onChangeContent && (
              <div className="geo-assistant-actions">
                <AiAnswerImporter
                  content={content}
                  onImport={onChangeContent}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline-primary"
                  onClick={applyTemplate}>
                  <Icon type="bi" name="file-earmark-plus" className="me-1" />
                  插入推荐结构
                </Button>
              </div>
            )}
          </div>

          <ProgressBar
            now={analysis.score}
            variant={variant}
            className="geo-score-bar"
          />

          <div className="geo-check-list">
            {[...failedChecks, ...passedChecks].map((check) => (
              <div
                key={check.id}
                className={classNames(
                  'geo-check-item',
                  check.passed ? 'is-passed' : 'is-missing',
                )}>
                <Icon
                  type="bi"
                  name={check.passed ? 'check-circle-fill' : 'circle'}
                  className="geo-check-icon"
                />
                <div>
                  <strong>{check.label}</strong>
                  <span>{check.hint}</span>
                </div>
              </div>
            ))}
          </div>

          {onChangeContent && (
            <div className="geo-evidence-box">
              <Form.Label className="geo-evidence-label">证据链来源</Form.Label>
              <Stack
                direction="horizontal"
                gap={2}
                className="align-items-start">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={sourceInput}
                  placeholder="每行粘贴一个来源，例如：Go 1.22 Release Notes: https://go.dev/doc/go1.22"
                  onChange={(evt) => setSourceInput(evt.target.value)}
                />
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="geo-evidence-action"
                  disabled={parsedSources.length === 0}
                  onClick={applyEvidenceSources}>
                  追加
                </Button>
              </Stack>
              {parsedSources.length > 0 && (
                <div className="geo-evidence-preview">
                  将追加 {parsedSources.length} 条来源
                </div>
              )}
            </div>
          )}

          {analysis.sourceUrls.length > 0 ? (
            <div className="geo-source-list">
              <span>已识别真实来源：</span>
              {analysis.sourceUrls.slice(0, 4).map((url) => (
                <a key={url} href={url} target="_blank" rel="noreferrer">
                  {url}
                </a>
              ))}
            </div>
          ) : (
            <Alert variant="light" className="geo-source-empty">
              还没有识别到真实来源链接。涉及版本、性能、规范、统计、官方说法时，请补官方文档、源码、Issue、Release、RFC
              或基准报告链接。
            </Alert>
          )}

          {analysis.missingSourceHints.length > 0 && (
            <Alert variant="warning" className="geo-warning">
              {analysis.missingSourceHints.map((hint) => (
                <div key={hint}>{hint}</div>
              ))}
            </Alert>
          )}
        </section>
      </Modal>
    </>
  );
};

export default memo(AnswerGeoAssistant);
